import { Component, DestroyRef, effect, inject, input, OnInit, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { BuilderCode, FiltreBuilder, LineThemeCode } from '@lib/services/filtrer-investisseurs/filtre-builders/filtre-builder.model';
import { FiltreBuilderService } from '@lib/services/filtrer-investisseurs/filtre-builders/filtre-builder.service';
import { FiltreLine } from '@lib/services/filtrer-investisseurs/filtre-lines/filtre-line.model';
import { numbersAndSeparatorsRegex, numbersRegex } from '@lib/utils/app-utils';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { SHARED_IMPORTS } from 'projects/minotaure-lib/src/shared-imports';
import { first, Subscription } from 'rxjs';

@Component({
  selector: 'app-filtre-custom-line',
  imports: [SHARED_IMPORTS],
  templateUrl: './filtre-custom-line.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./filtre-custom-line.component.scss'],
})
export class FiltreCustomLineComponent implements OnInit {
  line = input.required<FiltreLine>();
  index = input.required<number>();

  remove = output<number>();
  lineChange = output<{ index: number; line: FiltreLine }>();

  private destroyRef = inject(DestroyRef);
  numbersRegex = numbersRegex;
  numbersAndSeparatorsRegex = numbersAndSeparatorsRegex;

  builder = signal<FiltreBuilder | null>(null);
  currentOptions = signal<OuiSelectOption<any>[]>([]);
  optionsSubscription?: Subscription;
  isSettingUp = false;

  builderService = inject(FiltreBuilderService);

  themeControl!: FormControl<LineThemeCode | null>;
  itemControl!: FormControl<BuilderCode | null>;
  operatorControl!: FormControl<any>;
  valueControl!: FormControl<any>;

  readonly themeOptions = signal<OuiSelectOption<LineThemeCode>[]>([]);
  readonly itemOptions = signal<OuiSelectOption<BuilderCode>[]>([]);

  constructor() {
    effect(() => {
      const builder = this.builder();
      this.optionsSubscription?.unsubscribe();
      if (builder?.options) {
        this.optionsSubscription = builder.options.pipe(first(), takeUntilDestroyed(this.destroyRef)).subscribe(options => {
          this.currentOptions.set(options ?? []);
        });
      } else {
        this.currentOptions.set([]);
      }
    });
  }

  ngOnInit() {
    this.builder.set(this.line().builder ?? null);
    // Les builders BUTTON sont exclus par getSelectableCustomBuilders ; les thèmes restants sont triés par libellé.
    const themeCodes = new Set(this.builderService.getSelectableCustomBuilders().map(f => f.getThemeCode()));
    const options = Array.from(themeCodes)
      .map(themeCode => ({
        label: FiltreBuilder.getThemeLibelle(themeCode),
        value: themeCode,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    this.themeOptions.set(options);
    if (!this.line().builder) {
      this.getBuilderByItemCode(this.line().builderCode);
    }
    this.setControls();
  }

  getBuilderByItemCode(itemCode: BuilderCode): void {
    const foundBuilder = this.builderService.findBuilderByItemCode(itemCode);
    if (foundBuilder) {
      this.builder.set(foundBuilder);
      return;
    }
    throw new Error('Builder not found for item code: ' + itemCode);
  }
  setControls() {
    this.isSettingUp = true;
    this.themeControl = new FormControl(this.line().themeCode);
    this.setItemOptionsByThemeCode(this.line().themeCode);
    this.itemControl = new FormControl(this.line().builderCode);

    this.themeControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(themeCode => {
      if (themeCode && !this.isSettingUp) {
        this.setItemOptionsByThemeCode(themeCode);
        this.itemControl.setValue(this.itemOptions()[0].value ?? null);
      }
      this.isSettingUp = false;
    });

    this.itemControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(itemCode => {
      if (!itemCode) return;
      this.getBuilderByItemCode(itemCode);
      if (this.line().builderCode !== itemCode || this.line().operator === null) {
        if (this.builder) {
          this.operatorControl.setValue(this.builder()?.operatorOptions[0].value);
        }
        //reset la valeur du filtre
        this.valueControl.reset(null, { emitEvent: false });
      }
      this.emitUpdatedLine();
    });

    this.operatorControl = new FormControl(this.line().operator ?? this.builder()?.operatorOptions[0].value);
    this.operatorControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.emitUpdatedLine();
    });
    this.valueControl = new FormControl(this.line().value);
    this.valueControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      // check de la longueur max
      const maxLength = this.builder()?.maxInputLength;
      if (maxLength && value !== null && value !== undefined) {
        const strValue = value.toString();
        if (strValue.length > maxLength) {
          const truncatedString = strValue.substring(0, maxLength);
          const truncatedValue = Number(truncatedString);
          this.valueControl.patchValue(truncatedValue, { emitEvent: false });
        }
      }
      this.emitUpdatedLine();
    });
  }

  private getValueControlValue(): string | number | null {
    const builder = this.builder();
    const val = this.valueControl.value;

    if (val === null || val === undefined || val === '') {
      return null;
    }

    switch (builder?.inputType) {
      case 'Date':
        return this.toLocalDateString(val);

      case 'DateTime': {
        // 1. On récupère la date au format YYYY-MM-DD
        const localString = this.toLocalDateString(val);

        // 2. On la découpe pour recréer une date locale stricte à minuit
        const [year, month, day] = localString.split('-').map(Number);

        // Attention: Les mois commencent à 0 dans le constructeur de Date
        const localMidnight = new Date(year, month - 1, day, 0, 0, 0);

        // 3. On retourne l'ISO string complet (qui convertira automatiquement en UTC pour le backend)
        return localMidnight.toISOString();
      }

      case 'InputNumeric':
      case 'InputEuro':
      case 'InputPercent': {
        const num = Number(val);
        // /!\ Si isNaN, val est retourné comme string. Assure-toi que tes validateurs de form bloquent ça en amont.
        return isNaN(num) ? (val as string) : num;
      }

      default:
        return val as string | number;
    }
  }

  private toLocalDateString(value: unknown): string {
    // Si value est une string de la forme YYYY-MM-DD valide
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.slice(0, 10);
    }

    const parsed = value instanceof Date ? value : new Date(value as string | number);

    if (isNaN(parsed.getTime())) {
      return String(value);
    }

    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  setItemOptionsByThemeCode(themeCode: LineThemeCode) {
    this.itemOptions.set(
      this.builderService
        .getSelectableCustomBuilders()
        .filter(f => f.getThemeCode() === themeCode)
        .map(f => ({
          label: FiltreBuilder.getBuilderLibelle(f.getBuilderCode()) ?? '',
          value: f.getBuilderCode(),
        }))
    );
  }

  removeLine() {
    this.remove.emit(this.index());
  }

  private emitUpdatedLine() {
    const builder = this.builder();
    const updatedLine = this.line();
    updatedLine.operator = this.operatorControl.value;
    updatedLine.builderCode = this.itemControl.value ?? this.line().builderCode;
    updatedLine.themeCode = this.themeControl.value ?? this.line().themeCode;
    updatedLine.order = this.index();
    updatedLine.builder = builder ?? null;
    const lineWithValue = updatedLine.withValue(this.getValueControlValue());
    this.lineChange.emit({ index: this.index(), line: lineWithValue });
  }
}
